/**
 * TrustedContactPicker — focused dialog for the pre-meet wizard.
 *
 * Lets the member, in one place:
 *   1. See their existing Trusted Circle contacts at a glance.
 *   2. Quickly add a new contact (just name + phone or email).
 *
 * It's intentionally lighter than the full TrustedCirclePanel — the
 * wizard only needs the member to clear the "≥1 contact" gate so the
 * checklist can be saved.  Full edits stay in Account → Safety.
 */
import { useMemo, useState } from "react";
import { Loader2, Phone, Mail, Plus, CheckCircle2, X, Trash2, AlertCircle } from "lucide-react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  useTrustedCircle,
  TRUSTED_CIRCLE_MAX,
} from "@/lib/safety/useTrustedCircle";

// ---------- Validation schema (client-side; server enforces RLS) ----
// Phone: keep as a free-form display string but require ≥7 digits and
// only allow phone-shaped characters (digits, spaces, +, -, parens, dot,
// extension marker x). Strict E.164 would reject too many real-world
// formats members type from memory.
const PHONE_SHAPE_RE = /^[0-9+\-\s().x]+$/i;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15; // E.164 cap
const NAME_MAX = 80;
const REL_MAX = 40;
const EMAIL_MAX = 120;
const PHONE_MAX = 32;

const phoneSchema = z
  .string()
  .trim()
  .max(PHONE_MAX, `Phone must be ${PHONE_MAX} characters or fewer.`)
  .refine((v) => v === "" || PHONE_SHAPE_RE.test(v), {
    message: "Use digits, spaces, +, -, ( ), . or x only.",
  })
  .refine(
    (v) => {
      if (v === "") return true;
      const digits = v.replace(/\D+/g, "");
      return digits.length >= PHONE_MIN_DIGITS && digits.length <= PHONE_MAX_DIGITS;
    },
    {
      message: `Phone must have ${PHONE_MIN_DIGITS}–${PHONE_MAX_DIGITS} digits.`,
    },
  );

const emailSchema = z
  .string()
  .trim()
  .max(EMAIL_MAX, `Email must be ${EMAIL_MAX} characters or fewer.`)
  .refine((v) => v === "" || z.string().email().safeParse(v).success, {
    message: "Enter a valid email address.",
  });

const nameSchema = z
  .string()
  .trim()
  .min(2, "Name must be at least 2 characters.")
  .max(NAME_MAX, `Name must be ${NAME_MAX} characters or fewer.`);

const relSchema = z
  .string()
  .trim()
  .max(REL_MAX, `Relationship must be ${REL_MAX} characters or fewer.`);

type FieldKey = "name" | "phone" | "email" | "relationship" | "form";
type FieldErrors = Partial<Record<FieldKey, string>>;

interface TrustedContactPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after a contact is added or when the dialog closes with ≥1 contact. */
  onSatisfied?: () => void;
}

const TrustedContactPicker = ({
  open,
  onOpenChange,
  onSatisfied,
}: TrustedContactPickerProps) => {
  const { contacts, loading, add, remove } = useTrustedCircle();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [relationship, setRelationship] = useState("");
  const [saving, setSaving] = useState(false);
  // Tracks which contact id is currently being deleted, so only the
  // affected row shows a spinner / disabled state.
  const [removingId, setRemovingId] = useState<string | null>(null);
  // Per-field "touched" tracking. Errors are computed live from the
  // current field values; we just gate their visibility on touched so
  // the form doesn't yell at users on first paint.
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});

  const reset = () => {
    setName("");
    setPhone("");
    setEmail("");
    setRelationship("");
    setTouched({});
  };

  const handleClose = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      reset();
      if (contacts.length > 0) onSatisfied?.();
    }
  };

  // Light auto-formatting for phone: collapses runs of internal
  // whitespace but never strips digits or `+`. We don't enforce a
  // locale-specific format because members type from many regions.
  const formatPhone = (raw: string) =>
    raw.replace(/[ \t]{2,}/g, " ").replace(/^\s+/, "");

  // Run the schemas and surface per-field messages. Returns true when
  // the form is structurally valid AND at least one of phone/email is
  // present (we need a way to actually reach the contact).
  const validate = useMemo(
    () => (): { ok: boolean; errs: FieldErrors } => {
      const errs: FieldErrors = {};
      const nameRes = nameSchema.safeParse(name);
      if (!nameRes.success) errs.name = nameRes.error.issues[0]?.message;

      const phoneRes = phoneSchema.safeParse(phone);
      if (!phoneRes.success) errs.phone = phoneRes.error.issues[0]?.message;

      const emailRes = emailSchema.safeParse(email);
      if (!emailRes.success) errs.email = emailRes.error.issues[0]?.message;

      const relRes = relSchema.safeParse(relationship);
      if (!relRes.success) errs.relationship = relRes.error.issues[0]?.message;

      const phoneClean = phone.trim();
      const emailClean = email.trim();
      const hasReachable =
        (phoneClean !== "" && !errs.phone) ||
        (emailClean !== "" && !errs.email);
      if (!hasReachable && !errs.phone && !errs.email) {
        errs.form = "Add a phone number or email so we can reach them.";
      }

      return { ok: Object.keys(errs).length === 0, errs };
    },
    [name, phone, email, relationship],
  );

  const liveCheck = validate();
  const canSubmit = liveCheck.ok && contacts.length < TRUSTED_CIRCLE_MAX;
  // Only display per-field errors for fields the user has touched (or
  // after a submit attempt that flipped them all to touched).
  const visibleErrors: FieldErrors = {
    ...(touched.name ? { name: liveCheck.errs.name } : {}),
    ...(touched.phone ? { phone: liveCheck.errs.phone } : {}),
    ...(touched.email ? { email: liveCheck.errs.email } : {}),
    ...(touched.relationship
      ? { relationship: liveCheck.errs.relationship }
      : {}),
    ...(touched.form ? { form: liveCheck.errs.form } : {}),
  };

  const handleAdd = async () => {
    // Force every field to "touched" on submit attempt so any errors
    // become visible inline even if the user never blurred them.
    setTouched({
      name: true,
      phone: true,
      email: true,
      relationship: true,
      form: true,
    });
    const check = validate();
    if (!check.ok || contacts.length >= TRUSTED_CIRCLE_MAX) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    // Duplicate guard: a contact already exists if its name matches
    // (case-insensitive, trimmed) AND it shares either the phone or
    // the email being entered. Phone matching strips non-digits so
    // "+1 (555) 010-0123" and "5550100123" collide. We don't dedupe
    // on name alone — a member may legitimately have two "Sam"s.
    const normName = name.trim().toLowerCase();
    const normPhone = phone.replace(/\D+/g, "");
    const normEmail = email.trim().toLowerCase();
    const duplicate = contacts.find((c) => {
      if ((c.name ?? "").trim().toLowerCase() !== normName) return false;
      const cPhone = (c.phone ?? "").replace(/\D+/g, "");
      const cEmail = (c.email ?? "").trim().toLowerCase();
      const phoneMatch = normPhone.length >= 5 && cPhone === normPhone;
      const emailMatch = normEmail.length > 0 && cEmail === normEmail;
      return phoneMatch || emailMatch;
    });
    if (duplicate) {
      toast.error(`${duplicate.name} is already in your Trusted Circle.`, {
        description:
          "Same name and " +
          ((duplicate.phone ?? "").replace(/\D+/g, "") === normPhone &&
          normPhone.length >= 5
            ? "phone number"
            : "email") +
          " — edit the existing contact in Account → Safety instead.",
      });
      return;
    }

    setSaving(true);
    const { error } = await add({
      name: name.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      relationship: relationship.trim() || null,
      notify_on_press: true,
      notify_on_missed_checkin: true,
    });
    setSaving(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(`${name.trim()} added to your Trusted Circle.`);
    reset();
    onSatisfied?.();
  };

  const handleRemove = async (id: string, displayName: string) => {
    // Lightweight inline confirm — the wizard is already a modal, so
    // we can't stack a second Dialog cleanly.  window.confirm keeps
    // the destructive action explicit without UI gymnastics.
    if (!window.confirm(`Remove ${displayName} from your Trusted Circle?`)) {
      return;
    }
    setRemovingId(id);
    const { error } = await remove(id);
    setRemovingId(null);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(`${displayName} removed.`);
  };

  const atCap = contacts.length >= TRUSTED_CIRCLE_MAX;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Trusted Circle</DialogTitle>
          <DialogDescription>
            Pick or add at least one contact. They'll be notified about
            this meet — and any future safety check-ins or emergency
            presses.
          </DialogDescription>
        </DialogHeader>

        {/* Existing contacts */}
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Your contacts ({contacts.length}/{TRUSTED_CIRCLE_MAX})
          </p>
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : contacts.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
              No trusted contacts yet. Add one below to satisfy the
              pre-meet sharing requirement.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {contacts.map((c) => {
                const isRemoving = removingId === c.id;
                return (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {c.name}
                        {c.relationship && (
                          <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                            · {c.relationship}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate flex items-center gap-2">
                        {c.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </span>
                        )}
                        {c.email && (
                          <span className="inline-flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {c.email}
                          </span>
                        )}
                      </p>
                    </div>
                    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => void handleRemove(c.id, c.name)}
                      disabled={isRemoving}
                      aria-label={`Remove ${c.name} from Trusted Circle`}
                      title={`Remove ${c.name}`}
                      className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                    >
                      {isRemoving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Quick add form */}
        <div className="space-y-3 rounded-xl border border-border bg-secondary/30 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Quick add
          </p>
          {atCap ? (
            <p className="text-xs text-muted-foreground">
              You've reached the {TRUSTED_CIRCLE_MAX}-contact cap. Manage
              the full list in Account → Safety.
            </p>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Label htmlFor="tcp-name" className="text-xs">
                    Name
                  </Label>
                  <Input
                    id="tcp-name"
                    placeholder="Sam Diaz"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                    maxLength={NAME_MAX}
                    aria-invalid={!!visibleErrors.name}
                    aria-describedby={visibleErrors.name ? "tcp-name-err" : undefined}
                    className={
                      visibleErrors.name
                        ? "border-destructive focus-visible:ring-destructive/30"
                        : ""
                    }
                  />
                  {visibleErrors.name && (
                    <p
                      id="tcp-name-err"
                      className="mt-1 flex items-start gap-1 text-[11px] text-destructive"
                    >
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      {visibleErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="tcp-rel" className="text-xs">
                    Relationship (optional)
                  </Label>
                  <Input
                    id="tcp-rel"
                    placeholder="Sister, roommate…"
                    value={relationship}
                    onChange={(e) => setRelationship(e.target.value)}
                    onBlur={() =>
                      setTouched((t) => ({ ...t, relationship: true }))
                    }
                    maxLength={REL_MAX}
                    aria-invalid={!!visibleErrors.relationship}
                    aria-describedby={
                      visibleErrors.relationship ? "tcp-rel-err" : undefined
                    }
                    className={
                      visibleErrors.relationship
                        ? "border-destructive focus-visible:ring-destructive/30"
                        : ""
                    }
                  />
                  {visibleErrors.relationship && (
                    <p
                      id="tcp-rel-err"
                      className="mt-1 flex items-start gap-1 text-[11px] text-destructive"
                    >
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      {visibleErrors.relationship}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="tcp-phone" className="text-xs">
                    Phone
                  </Label>
                  <Input
                    id="tcp-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+1 555 010 0123"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    onBlur={() => setTouched((t) => ({ ...t, phone: true, form: true }))}
                    maxLength={PHONE_MAX}
                    aria-invalid={!!visibleErrors.phone}
                    aria-describedby={visibleErrors.phone ? "tcp-phone-err" : "tcp-phone-help"}
                    className={
                      visibleErrors.phone
                        ? "border-destructive focus-visible:ring-destructive/30"
                        : ""
                    }
                  />
                  {visibleErrors.phone ? (
                    <p
                      id="tcp-phone-err"
                      className="mt-1 flex items-start gap-1 text-[11px] text-destructive"
                    >
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      {visibleErrors.phone}
                    </p>
                  ) : (
                    <p
                      id="tcp-phone-help"
                      className="mt-1 text-[11px] text-muted-foreground"
                    >
                      {PHONE_MIN_DIGITS}–{PHONE_MAX_DIGITS} digits, country code optional.
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="tcp-email" className="text-xs">
                    Email
                  </Label>
                  <Input
                    id="tcp-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="sam@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true, form: true }))}
                    maxLength={EMAIL_MAX}
                    aria-invalid={!!visibleErrors.email}
                    aria-describedby={visibleErrors.email ? "tcp-email-err" : undefined}
                    className={
                      visibleErrors.email
                        ? "border-destructive focus-visible:ring-destructive/30"
                        : ""
                    }
                  />
                  {visibleErrors.email && (
                    <p
                      id="tcp-email-err"
                      className="mt-1 flex items-start gap-1 text-[11px] text-destructive"
                    >
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      {visibleErrors.email}
                    </p>
                  )}
                </div>
              </div>
              {visibleErrors.form ? (
                <p
                  role="alert"
                  className="flex items-start gap-1 text-[11px] text-destructive"
                >
                  <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  {visibleErrors.form}
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  At least one of phone or email is required so we can
                  actually reach them.
                </p>
              )}
              <Button
                size="sm"
                onClick={() => void handleAdd()}
                disabled={saving || (Object.keys(touched).length > 0 && !canSubmit)}
                className="w-full"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Add contact
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <p className="text-[11px] text-muted-foreground">
            Manage details, notify rules, and ordering in Account → Safety.
          </p>
          <Button
            variant={contacts.length > 0 ? "default" : "outline"}
            size="sm"
            onClick={() => handleClose(false)}
          >
            {contacts.length > 0 ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Done
              </>
            ) : (
              <>
                <X className="h-4 w-4 mr-1" /> Close
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrustedContactPicker;
